import React, { createContext, useContext, useState, useCallback, useMemo, PropsWithChildren } from 'react';
import {
  expensesService,
  Expense,
  UpdateExpenseInput,
  usersService,
  flightDetailsService,
} from '../services/supabase';
import { useAuth } from './AuthContext';
import { useConversion, ExpenseInputWithoutConversion } from '../hooks/useConversion';

// Helper function to extract date from flight description text
const extractDateFromText = (text: string, expenseDate?: string): string | null => {
  if (!text) return null;

  // Get year from expense date for patterns without year
  const yearFromExpense = expenseDate ? new Date(expenseDate).getFullYear() : new Date().getFullYear();

  // Common date patterns
  const patternsWithYear = [
    // ISO format: YYYY-MM-DD or YYYY/MM/DD
    /(\d{4})[-/](\d{1,2})[-/](\d{1,2})/,
    // US format: MM/DD/YYYY or MM-DD-YYYY
    /(\d{1,2})[-/](\d{1,2})[-/](\d{4})/,
    // Format with month names: Jan 15, 2024 or 15 Jan 2024
    /(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+(\d{4})/i,
    /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+(\d{1,2}),?\s+(\d{4})/i,
  ];

  // Patterns without year - we'll infer the year
  const patternsWithoutYear = [
    // Month name followed by day: "Oct 5", "October 5"
    /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+(\d{1,2})/i,
    // Day followed by month name: "5 Oct", "5 October"
    /(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*/i,
  ];

  // Try patterns with year first
  for (const pattern of patternsWithYear) {
    const match = text.match(pattern);
    if (match) {
      try {
        const dateStr = match[0];
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0]; // Return YYYY-MM-DD format
        }
      } catch (e) {
        continue;
      }
    }
  }

  // Try patterns without year, and append the year from expense date
  for (const pattern of patternsWithoutYear) {
    const match = text.match(pattern);
    if (match) {
      try {
        const dateStr = `${match[0]} ${yearFromExpense}`;
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0]; // Return YYYY-MM-DD format
        }
      } catch (e) {
        continue;
      }
    }
  }

  return null;
};

interface ExpensesContextType {
  expenses: Expense[];
  loading: boolean;
  error: string | null;
  nonFlightExpenses: Expense[];
  averageNonFlightExpense: number;
  fetchTripExpenses: (tripId: string) => Promise<void>;
  fetchExpensesByCategory: (tripId: string, category: string) => Promise<void>;
  fetchPlannedExpenses: (tripId: string, isPlanned: boolean) => Promise<void>;
  createExpense: (input: ExpenseInputWithoutConversion) => Promise<Expense>;
  updateExpense: (expenseId: string, input: UpdateExpenseInput) => Promise<Expense>;
  deleteExpense: (expenseId: string) => Promise<void>;
  clearExpenses: () => void;
}

const ExpensesContext = createContext<ExpensesContextType | undefined>(undefined);

export const ExpensesProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { firebaseUser } = useAuth();
  const { prepareExpenseWithConversion } = useConversion();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Calculate non-flight expenses
  const nonFlightExpenses = useMemo(() => {
    return expenses.filter(expense => {
      const category = expense.category?.toLowerCase() || '';
      return !category.includes('flight');
    });
  }, [expenses]);

  // Calculate average non-flight expense
  const averageNonFlightExpense = useMemo(() => {
    if (nonFlightExpenses.length === 0) return 0;
    const total = nonFlightExpenses.reduce((sum, expense) => sum + expense.amount, 0);
    return total / nonFlightExpenses.length;
  }, [nonFlightExpenses]);

  const fetchTripExpenses = useCallback(async (tripId: string) => {
    setLoading(true);
    setError(null);

    try {
      const data = await expensesService.getTripExpenses(tripId);
      setExpenses(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch expenses';
      setError(errorMessage);
      console.error('Error fetching expenses:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchExpensesByCategory = useCallback(async (tripId: string, category: string) => {
    setLoading(true);
    setError(null);

    try {
      const data = await expensesService.getExpensesByCategory(tripId, category);
      setExpenses(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch expenses by category';
      setError(errorMessage);
      console.error('Error fetching expenses by category:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPlannedExpenses = useCallback(async (tripId: string, isPlanned: boolean) => {
    setLoading(true);
    setError(null);

    try {
      const data = await expensesService.getPlannedExpenses(tripId, isPlanned);
      setExpenses(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch planned expenses';
      setError(errorMessage);
      console.error('Error fetching planned expenses:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createExpense = useCallback(
    async (input: ExpenseInputWithoutConversion): Promise<Expense> => {
      console.log('=== ExpensesContext.createExpense called ===');
      console.log('Input:', input);

      if (!firebaseUser) {
        console.error('No firebase user - throwing error');
        throw new Error('User must be authenticated to create an expense');
      }

      setLoading(true);
      setError(null);

      try {
        // Get user's preferred currency
        const user = await usersService.getUserById(firebaseUser.uid);
        const userCurrency = user?.preferred_currency || 'USD';
        console.log('User currency:', userCurrency);

        // Prepare expense with currency conversion
        const expenseWithConversion = await prepareExpenseWithConversion(input, userCurrency);
        console.log('Expense with conversion:', expenseWithConversion);

        const newExpense = await expensesService.createExpense(firebaseUser.uid, expenseWithConversion);
        console.log('Created expense:', newExpense);

        // Auto-create flight_details if this is a flight expense
        const category = input.category?.toLowerCase() || '';
        if (category.includes('flight')) {
          try {
            // Extract date from description, using expense_date to infer year if needed
            const extractedDate = extractDateFromText(input.description, input.expense_date);
            console.log('Creating flight_details with extracted date:', extractedDate);

            await flightDetailsService.create({
              expense_id: newExpense.id,
              trip_id: input.trip_id,
              flight_class: 'Economy', // Default class
              actual_flight_date: extractedDate || input.expense_date, // Use extracted date or fall back to expense date
            });
            console.log('Flight details created successfully');
          } catch (flightDetailsError) {
            // Don't fail the whole expense creation if flight details fail
            console.error('Error creating flight details:', flightDetailsError);
          }
        }

        setExpenses((prev) => [newExpense, ...prev]);
        return newExpense;
      } catch (err) {
        console.error('Error in createExpense:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to create expense';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [firebaseUser, prepareExpenseWithConversion]
  );

  const updateExpense = useCallback(
    async (expenseId: string, input: UpdateExpenseInput): Promise<Expense> => {
      setLoading(true);
      setError(null);

      try {
        const updatedExpense = await expensesService.updateExpense(expenseId, input);
        setExpenses((prev) =>
          prev.map((expense) => (expense.id === expenseId ? updatedExpense : expense))
        );
        return updatedExpense;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update expense';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const deleteExpense = useCallback(async (expenseId: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      await expensesService.deleteExpense(expenseId);
      setExpenses((prev) => prev.filter((expense) => expense.id !== expenseId));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete expense';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearExpenses = useCallback(() => {
    setExpenses([]);
    setError(null);
  }, []);

  return (
    <ExpensesContext.Provider
      value={{
        expenses,
        loading,
        error,
        nonFlightExpenses,
        averageNonFlightExpense,
        fetchTripExpenses,
        fetchExpensesByCategory,
        fetchPlannedExpenses,
        createExpense,
        updateExpense,
        deleteExpense,
        clearExpenses,
      }}
    >
      {children}
    </ExpensesContext.Provider>
  );
};

export const useExpenses = () => {
  const context = useContext(ExpensesContext);
  if (context === undefined) {
    throw new Error('useExpenses must be used within an ExpensesProvider');
  }
  return context;
};
